import React from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import { useIntl } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import PhoneInput, { isPossiblePhoneNumber } from 'react-phone-number-input';
import '../../../styles/PhoneNumberInputGlobal.css';
import {
  Form,
  PrimaryButton,
  FieldTextInput,
  FieldMultiSelect,
  FieldRadioButton,
} from '../../../components';
import { useConfiguration } from '../../../context/configurationContext';
import css from './ShippingDetailsForm.module.css';
import getCountryCodes from '../../../translations/countryCodes';
import { ShippingPreference } from '../../../util/enums';
import PackageDimensionSection from './PackageDimensionSection';
import { GB } from '../../../util/constants';

const ShippingDetailsForm = props => (
  <FinalForm
    {...props}
    render={({
      className,
      rootClassName,
      disabled,
      handleSubmit,
      formId,
      invalid,
      ready,
      pristine,
      values,
      form,
      inProgress,
    }) => {
      const config = useConfiguration();
      const intl = useIntl();
      const { country, shippingPreference } = values || {};
      const classes = classNames(rootClassName || css.root, className);
      const submitDisabled = invalid || disabled || inProgress;
      const countryCodes = getCountryCodes(config.localization.locale);
      const isCountryUK = country?.code === GB;
      const fields = [
        {
          name: 'street1',
          type: 'text',
          autoComplete: 'street-address',
          messageId: 'streetAddress',
        },
        ...(isCountryUK
          ? []
          : [
              {
                name: 'street2',
                type: 'text',
                autoComplete: 'address-line2', // correct for secondary line (apt, suite, etc.)
                messageId: 'buildingNumber', // or 'addressLine2' if you prefer
              },
            ]),
        { name: 'city', type: 'text', autoComplete: 'address-level2', messageId: 'city' },
        {
          name: 'state',
          type: 'text',
          autoComplete: 'address-level1',
          messageId: isCountryUK ? 'county' : 'state',
        },
        {
          name: 'zip',
          type: 'text',
          autoComplete: 'postal-code',
          messageId: isCountryUK ? 'postCode' : 'postalCode',
        },
      ];
      const renderField = ({ name, type, autoComplete, messageId }) => (
        <FieldTextInput
          type={type}
          id={formId ? `${formId}.${name}` : name}
          name={name}
          autoComplete={autoComplete}
          label={intl.formatMessage({ id: `ShippingDetailsForm.${messageId}` })}
          placeholder={intl.formatMessage({ id: `ShippingDetailsForm.${messageId}Placeholder` })}
          validate={validators.required(
            intl.formatMessage({ id: `ShippingDetailsForm.${messageId}Required` })
          )}
        />
      );

      const renderSenderAddress = (
        <div className={classNames(css.section, css.addressSection)}>
          <h3 className={css.sectionTitle}>
            {intl.formatMessage({ id: 'ShippingDetailsForm.senderAddress' })}
          </h3>

          {/* Dynamic address layout based on country */}
          {isCountryUK ? (
            <>
              {/* Row 1: Street + City */}
              <div className={css.formRow}>
                {['street1', 'city'].map(fieldName => {
                  const field = fields.find(f => f.name === fieldName);
                  return field ? (
                    <div className={css.formFld} key={fieldName}>
                      {renderField(field)}
                    </div>
                  ) : null;
                })}
              </div>

              {/* Row 2: County + Postcode */}
              <div className={css.formRow}>
                {['state', 'zip'].map(fieldName => {
                  const field = fields.find(f => f.name === fieldName);
                  return field ? (
                    <div className={css.formFld} key={fieldName}>
                      {renderField(field)}
                    </div>
                  ) : null;
                })}
              </div>
            </>
          ) : (
            <>
              {/* Non-UK: Original layout */}
              <div className={css.formRow}>
                {['street1', 'street2'].map(fieldName => {
                  const field = fields.find(f => f.name === fieldName);
                  return field ? (
                    <div className={css.formFld} key={fieldName}>
                      {renderField(field)}
                    </div>
                  ) : null;
                })}
              </div>

              <div className={css.formRow}>
                {['city', 'state'].map(fieldName => {
                  const field = fields.find(f => f.name === fieldName);
                  return field ? (
                    <div className={css.formFld} key={fieldName}>
                      {renderField(field)}
                    </div>
                  ) : null;
                })}
              </div>

              {/* Zip alone in its own row */}
              <div className={css.formRow}>
                <div className={css.formFld}>
                  {fields.find(f => f.name === 'zip') &&
                    renderField(fields.find(f => f.name === 'zip'))}
                </div>
              </div>
            </>
          )}

          {/* Country + Phone row — always same */}
          <div className={css.formRow}>
            <div className={css.formFld}>
              <Field
                name="country"
                label={intl.formatMessage({ id: 'ShippingDetailsForm.country' })}
                component={FieldMultiSelect}
                options={countryCodes}
                isMulti={false}
                validate={validators.required(
                  intl.formatMessage({ id: 'ShippingDetailsForm.countryRequired' })
                )}
              />
            </div>
            <div className={css.formFld}>
              {/* Phone field remains unchanged */}
              <Field
                name="phone"
                validate={value =>
                  !value
                    ? intl.formatMessage({ id: 'ShippingDetails.phoneRequired' })
                    : isPossiblePhoneNumber(value)
                    ? undefined
                    : intl.formatMessage({ id: 'ShippingDetails.phoneNumberInvalid' })
                }
              >
                {({ input, meta }) => (
                  <>
                    <label className={css.phoneNumberLabel}>
                      {intl.formatMessage({ id: 'ShippingDetailsForm.phone' })}
                    </label>

                    <PhoneInput
                      international
                      withCountryCallingCode
                      defaultCountry="GB"
                      placeholder={intl.formatMessage({
                        id: 'ShippingDetailsForm.phonePlaceholder',
                      })}
                      value={input.value || ''}
                      onChange={input.onChange}
                      onBlur={() => input.onBlur(input.value)}
                      limitMaxLength={true}
                    />

                    {(meta.error || meta.submitError) && (meta.touched || meta.submitFailed) && (
                      <span className={css.invalidPhoneValue}>
                        {meta.error || meta.submitError}
                      </span>
                    )}
                  </>
                )}
              </Field>
            </div>
          </div>
        </div>
      );

      const preferredShippingMethod = (
        <div className={css.section}>
          <h3 className={css.sectionTitle}>
            {intl.formatMessage({
              id: 'ShippingDetailsForm.preferredShippingMethod',
            })}
          </h3>
          <div className={css.formRow}>
            <div className={css.formFld}>
              <FieldRadioButton
                id={`${formId}.platform`}
                name="shippingPreference"
                label={intl.formatMessage({
                  id: 'ShippingDetailsForm.platform',
                })}
                value={ShippingPreference.PLATFORM}
                checkedClassName={css.checkedNotAvailable}
                showAsRequired={pristine}
              />
            </div>
            {/* <div className={css.formFld}>
              <FieldRadioButton
                id={`${formId}.own`}
                name="shippingPreference"
                label={intl.formatMessage({
                  id: 'ShippingDetailsForm.own',
                })}
                value={ShippingPreference.OWN}
                checkedClassName={css.own}
                showAsRequired={pristine}
              />
            </div> */}
          </div>
        </div>
      );

      const isOwnShipping = shippingPreference === ShippingPreference.OWN;

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          {preferredShippingMethod}
          {!isOwnShipping && (
            <>
              <PackageDimensionSection
                formId={formId}
                heading={
                  <h3 className={css.sectionTitle}>
                    {intl.formatMessage({
                      id: 'ShippingDetailsForm.defaultPackageSize',
                    })}
                  </h3>
                }
              />
              {renderSenderAddress}
            </>
          )}
          <PrimaryButton
            className={css.submitButton}
            type="submit"
            inProgress={inProgress}
            disabled={submitDisabled}
            ready={ready}
          >
            {intl.formatMessage({ id: 'ShippingDetailsForm.saveDetails' })}
          </PrimaryButton>
        </Form>
      );
    }}
  />
);

export default ShippingDetailsForm;
